import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import PlatformTabs from "@/components/PlatformTabs";
import StatsOverview from "@/components/StatsOverview";
import LinkCard from "@/components/LinkCard";
import AddEditLinkModal from "@/components/AddEditLinkModal";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Ghost } from "lucide-react";
import BatLogo from "@/components/BatLogo";

export default function Dashboard() {
  const [platforms, setPlatforms] = useState([]);
  const [links, setLinks] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadPlatforms = async () => {
    try {
      const { data } = await api.get("/platforms");
      setPlatforms(data);
      if (data.length && !activeId) setActiveId(data[0].id);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return []; }
  };
  // Refresh both platforms and links so custom platforms added on other
  // devices also show up on the next action here.
  const onPlatformsChanged = async () => {
    await loadPlatforms();
    await loadLinks();
  };
  const loadLinks = async () => {
    try {
      const { data } = await api.get("/links");
      setLinks(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadPlatforms(), loadLinks()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = useMemo(() => {
    const c = {};
    for (const l of links) c[l.platform_id] = (c[l.platform_id] || 0) + 1;
    return c;
  }, [links]);

  const platformMap = useMemo(() => {
    const m = {};
    for (const p of platforms) m[p.id] = p;
    return m;
  }, [platforms]);

  const globalStats = useMemo(() => {
    const s = { total: links.length, value: 0, pending: 0, ongoing: 0, completed: 0, submitted: 0 };
    for (const l of links) {
      s.value += Number(l.price || 0);
      s[l.status] = (s[l.status] || 0) + 1;
    }
    return s;
  }, [links]);

  const visibleLinks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return links.filter((l) => {
      if (l.platform_id !== activeId) return false;
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (!q) return true;
      return (
        l.url.toLowerCase().includes(q) ||
        (l.buyer_name || "").toLowerCase().includes(q) ||
        (l.vendor_name || "").toLowerCase().includes(q)
      );
    });
  }, [links, activeId, statusFilter, search]);

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (link) => { setEditing(link); setModalOpen(true); };

  // After every mutation we refetch from the server so changes made on other
  // devices become visible on this device (per the sync plan: refresh on load
  // and after every action; no polling, no websockets).
  const onSaved = async () => { await loadLinks(); };
  const onDeleted = async () => { await loadLinks(); };
  const onStatusChanged = async () => { await loadLinks(); };

  const activePlatform = platformMap[activeId];

  const filters = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "ongoing", label: "Ongoing" },
    { key: "completed", label: "Completed" },
    { key: "submitted", label: "Submitted" },
  ];

  return (
    <div className="min-h-screen">
      <Header
        onAddLink={openAdd}
        search={search}
        setSearch={setSearch}
        stats={{ total: globalStats.total, value: globalStats.value, pending: globalStats.pending }}
      />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8">
        <div className="mb-8">
          <div className="flex items-end justify-between mb-2">
            <div>
              <div className="label-mono">// Dashboard</div>
              <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-slate-100">
                Command <span className="text-amber-500">Overview</span>
              </h1>
            </div>
          </div>
          <StatsOverview stats={globalStats} />
        </div>

        <div className="mb-4">
          <div className="label-mono mb-3">// Platforms</div>
          <PlatformTabs
            platforms={platforms}
            activeId={activeId}
            setActiveId={setActiveId}
            counts={counts}
            onPlatformsChange={onPlatformsChanged}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 rounded text-xs font-display uppercase tracking-wider border transition
                  ${statusFilter === f.key
                    ? "border-amber-500 text-amber-300 bg-amber-500/10"
                    : "border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-600"}`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="label-mono text-slate-500">
            {visibleLinks.length} of {counts[activeId] || 0} shown
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span className="label-mono">Loading records…</span>
          </div>
        ) : visibleLinks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="card-tactical chamfered p-12 text-center"
            data-testid="empty-state"
          >
            <div className="text-amber-500/70 flex justify-center mb-4"><BatLogo size={72} glow={false} /></div>
            <h3 className="font-display uppercase text-2xl font-bold text-slate-200">
              No records in <span className="text-amber-400">{activePlatform?.name || "this section"}</span>
            </h3>
            <p className="text-slate-500 mt-2 max-w-md mx-auto text-sm">
              Start tracking creator collaborations, promo links, and vendor submissions.
              Every link stays synced across every device you log in from.
            </p>
            <button onClick={openAdd} className="btn-signal mt-6 px-6 py-2.5 rounded" data-testid="empty-add-btn">
              + Track Your First Link
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5" data-testid="links-grid">
            <AnimatePresence>
              {visibleLinks.map((l, i) => (
                <LinkCard
                  key={l.id}
                  link={l}
                  platform={platformMap[l.platform_id]}
                  onEdit={openEdit}
                  onDeleted={onDeleted}
                  onStatusChanged={onStatusChanged}
                  index={i}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      <footer className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-8 text-center label-mono text-slate-600">
        // Vrax's Workflow • Persistent across every device you sign in from
      </footer>

      <AddEditLinkModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        platforms={platforms}
        defaultPlatformId={activeId}
        editing={editing}
        onSaved={onSaved}
      />
    </div>
  );
}
